import { API_BASE_URL } from '@/lib/api/env';
import { getActiveOrganizationId } from '@/lib/api/active-org';
import { getAccessToken, notifyUnauthorized } from '@/lib/api/auth-token';
import type { ApiEnvelope } from '@/lib/api/types';

/** A decoded backend error (docs/17's `{ success: false, error: { code, message } }`). */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** A response that could not even be decoded as the docs/17 envelope. */
export class ApiUnexpectedResponseError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Unexpected response (HTTP ${String(status)}).`);
    this.name = 'ApiUnexpectedResponseError';
    this.status = status;
  }
}

/**
 * Thrown client-side, before any network call, when a `tenantScoped`
 * request has no Active Organization to name (ADR-026 Addendum, AO-15).
 * The organization-selection gate (`app/require-active-organization.tsx`)
 * is meant to prevent this from ever being reachable in the UI; this is
 * the defense-in-depth backstop so a tenant request can never silently
 * proceed as if it were valid.
 */
export class MissingActiveOrganizationError extends Error {
  constructor() {
    super('This request requires an Active Organization, but none is selected.');
    this.name = 'MissingActiveOrganizationError';
  }
}

export interface ApiFetchOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  /** Explicitly opt out of attaching the Bearer header (e.g. login, register). */
  readonly skipAuth?: boolean;
  /**
   * Attaches `X-Organization-Id` from the current Active Organization
   * (ADR-026 Addendum, AO-2). Opt-in and defaults to `false` - a request
   * must declare itself tenant-scoped; pages never set this header
   * themselves. Guardian invitation acceptance and every `/auth/*` route
   * stay `false` on purpose (AO-12).
   */
  readonly tenantScoped?: boolean;
  /**
   * Extra headers a specific call genuinely needs (Phase G-7, Job 9: the
   * session-management endpoints read `X-Session-Family-Id` to identify the
   * caller's own current session). Merged in after the Bearer/tenant
   * headers this client already manages, so a caller cannot use this to
   * override them - only to add something this client has no existing
   * concept of.
   */
  readonly headers?: Record<string, string>;
}

async function decodeEnvelope<T>(response: Response): Promise<ApiEnvelope<T>> {
  const text = await response.text();
  if (text.length === 0) {
    // 204 No Content and similar - a successful call with no body. Callers
    // that expect this pass a `T` of `null`.
    return { success: true, message: 'Success', data: null as T };
  }
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    throw new ApiUnexpectedResponseError(response.status);
  }
}

/**
 * The one shared HTTP client (ADR-026, Decision F2/F5). No component or
 * page calls `fetch` directly; every request goes through this function so
 * envelope decoding, the Bearer header, and 401 handling live in exactly
 * one place.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  // Caller-supplied headers are applied first, so the Bearer and tenant
  // headers this client manages below always win if a caller's `headers`
  // object happened to collide with one of those names.
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...options.headers,
  };

  if (options.skipAuth !== true) {
    const token = getAccessToken();
    if (token !== null) {
      headers['authorization'] = `Bearer ${token}`;
    }
  }

  if (options.tenantScoped === true) {
    const organizationId = getActiveOrganizationId();
    if (organizationId === null) {
      throw new MissingActiveOrganizationError();
    }
    headers['x-organization-id'] = organizationId;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : null,
  });

  const envelope = await decodeEnvelope<T>(response);

  if (envelope.success) {
    return envelope.data;
  }

  if (response.status === 401) {
    notifyUnauthorized();
  }

  throw new ApiError(response.status, envelope.error.code, envelope.error.message);
}
